import { NavLink, useNavigate, useParams } from 'react-router-dom'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../store/auth/authSlice'
import React, { useState } from 'react'
import { User } from '../../models/User'
import { TableColumn } from 'react-data-table-component'
import { ActionButton } from '../../components/DataTable/ActionButton'
import { Col, Container, Dropdown, Form, Row } from 'react-bootstrap'
import { CustomDropdownItem } from '../../components/Dropdown/CustomDropdownItem'
import { EditContactModal } from './EditContactModal'
import { DeleteContactModal } from './DeleteContactModal'
import { ZocksDataTable } from '../../components/DataTable/ZocksDataTable'

import { DEFAULT_PAGE_SIZE } from '../../common/constants'
import { MissingScopeAction } from '../../components/ScopedBlock/models'
import { ScopedBlock } from '../../components/ScopedBlock/ScopedBlock'
import { Roles } from '../../components/Roles/Roles'
import { useGetExternalUsersQuery } from '../../store/externalUser/externalUser'
import buttonStyles from '../../components/Button/Button.module.scss'
import { useDebounce } from 'use-debounce'
import { useCookies } from 'react-cookie'
import { useDocumentTitle } from '../../common/useDocumentTitle'
import { AddContactModal } from './AddContactModal'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { Button } from '../../components/Button/Button'
import { useZocksQuota } from '../../components/ScopedBlock/hooks'
import { ContactActionUpgradeBadge } from './Contact'

const SHOW_ONLY_MY_CONTACTS_COOKIE_MAX_AGE = 60 * 60 * 24 * 365

export const Contacts = () => {
  useDocumentTitle('Contacts')

  const { userId = null, modal = null } = useParams()
  const navigate = useNavigate()
  const { hasQuota } = useZocksQuota()

  const { tracked } = useTracking()

  const accountId = useAppSelector(selectAccountId)
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)

  const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE)
  const [page, setPage] = useState(1)
  const [selectedUserId, setSelectedUserId] = useState<string | null>(userId)

  const [showAddContactModal, setShowAddContactModal] = useState<boolean>(false)
  const [showEditContactModal, setShowEditContactModal] = useState<boolean>(modal === 'edit')
  const [showDeleteContactModal, setShowDeleteContactModal] = useState<boolean>(false)

  const [contactFilter, setContactFilter] = useState<string>('')
  const [debouncedContactFilter] = useDebounce(contactFilter, 400)
  const [cookies, setCookie] = useCookies(['showOnlyMyContacts'])
  const [showOnlyMyContacts, setShowOnlyMyContacts] = useState<boolean>(cookies.showOnlyMyContacts ?? false)

  const documentSummaryEnabled = hasQuota('ai.documentSummaryAgent.value')

  const {
    data: contactsResponse,
    isFetching,
    refetch: refetchContacts,
  } = useGetExternalUsersQuery(
    {
      accountId,
      name: debouncedContactFilter,
      ...(showOnlyMyContacts && { ownerId: currentUserId }),
      pageRequest: { page: page - 1, size: perPage },
    },
    { refetchOnMountOrArgChange: true },
  )

  const contacts = contactsResponse?.data ?? []

  const handlePageChange = (page: number) => {
    setPage(page)
  }

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPage(page)
    setPerPage(newPerPage)
  }

  const _handleOnClickAddNewContact = () => setShowAddContactModal(true)

  const handleOnClickAddNewContact = tracked(_handleOnClickAddNewContact, { eventName: TrackEvent.ADD_CONTACT_CLICKED })

  const _handleOnClickUploadDocument = () => {
    navigate(`/library/manage-contacts/contacts/documents/process`)
  }

  const handleOnClickUploadDocument = tracked(_handleOnClickUploadDocument, { eventName: TrackEvent.DOCUMENT_PROSPECTING_STARTED })

  const handleOnClickEditContact = (user: User) => {
    setSelectedUserId(user.id)
    setShowEditContactModal(true)
    navigate(`/library/manage-contacts/contacts/${user.id}/edit`, { replace: true })
  }

  const handleOnClickShowContact = (user: User) => {
    navigate(`/library/manage-contacts/contacts/${user.id}`)
  }

  const handleOnClickDeleteContact = (user: User) => {
    setSelectedUserId(user.id)
    setShowDeleteContactModal(true)
  }

  const handleAddContactModalSave = () => {
    setShowAddContactModal(false)
    navigate(0)
  }

  const handleAddContactModalCancel = () => {
    setShowAddContactModal(false)
  }

  const handleEditContactModalSave = () => {
    setShowEditContactModal(false)
    setSelectedUserId(null)
    refetchContacts()
    navigate(`/library/manage-contacts/`, { replace: true })
  }

  const handleEditContactModalCancel = () => {
    setShowEditContactModal(false)
    setSelectedUserId(null)
    navigate(`/library/manage-contacts/`, { replace: true })
  }

  const handleDeleteContactModalDelete = () => {
    setShowDeleteContactModal(false)
    setSelectedUserId(null)
    refetchContacts()
  }

  const handleDeleteContactModalCancel = () => {
    setShowDeleteContactModal(false)
    setSelectedUserId(null)
  }

  const handleContactFilterChange = async (filter: string) => {
    setContactFilter(filter)
  }

  const columns: TableColumn<User>[] = [
    {
      name: 'Name',
      selector: (row) => row.name,
      sortable: true,
      grow: 1,
      cell: (row) => (
        <NavLink className={`${buttonStyles.button} ${buttonStyles.linkButton} button-link-small`} to={`/library/manage-contacts/contacts/${row.id}`}>
          {row.name}
        </NavLink>
      ),
    },
    {
      name: 'Email',
      selector: (row) => row.email,
      sortable: true,
      grow: 2,
      width: '25%',
    },
    {
      name: 'Role',
      selector: (row) => JSON.stringify(row.roles.map((role) => role.id)),
      sortable: true,
      wrap: true,
      cell: (row) => <Roles userRoleIds={row.roles.map((role) => role.id)} />,
    },
    {
      ignoreRowClick: true,
      right: true,
      button: true,
      allowOverflow: true,
      width: '3rem',
      cell: (row) => (
        <ActionButton>
          <ScopedBlock
            scope={{
              scope: 'ext_user:read',
              superScope: ['ext_user:admin'],
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
          >
            <Dropdown.Item as={CustomDropdownItem} onClick={() => handleOnClickShowContact(row)}>
              Show
            </Dropdown.Item>
          </ScopedBlock>
          <ScopedBlock
            scope={{
              scope: 'ext_user:update',
              superScope: ['ext_user:admin'],
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
          >
            <Dropdown.Item as={CustomDropdownItem} onClick={() => handleOnClickEditContact(row)}>
              Edit
            </Dropdown.Item>
          </ScopedBlock>
          <ScopedBlock
            scope={{
              scope: 'ext_user:delete',
              superScope: ['ext_user:admin'],
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
          >
            <Dropdown.Item as={CustomDropdownItem} onClick={() => handleOnClickDeleteContact(row)}>
              Delete
            </Dropdown.Item>
          </ScopedBlock>
        </ActionButton>
      ),
    },
  ]

  return (
    <Container className='p-0'>
      <AddContactModal show={showAddContactModal} onSave={handleAddContactModalSave} onCancel={handleAddContactModalCancel} />
      {selectedUserId ? (
        <EditContactModal userId={selectedUserId} show={showEditContactModal} onSave={handleEditContactModalSave} onCancel={handleEditContactModalCancel} />
      ) : null}
      {selectedUserId ? (
        <DeleteContactModal
          user={contacts.find((user) => user.id === selectedUserId)}
          show={showDeleteContactModal}
          onDelete={handleDeleteContactModalDelete}
          onCancel={handleDeleteContactModalCancel}
        />
      ) : null}
      <Row className='mb-2'>
        <Col className='d-flex align-items-center justify-content-between'>
          <ScopedBlock
            scope={{
              scope: 'ext_user:create',
              superScope: ['ext_user:admin'],
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
          >
            <div className='d-flex align-items-center gap-3'>
              <Button variant='primary' className='px-3 py-2' onClick={handleOnClickAddNewContact}>
                <i className='bi bi-plus-circle' />
                Add manually
              </Button>
              <Button variant='grayGhost' className='px-3 py-2 position-relative' onClick={handleOnClickUploadDocument}>
                {!documentSummaryEnabled ? <ContactActionUpgradeBadge /> : null}
                <i className='bi bi-file-earmark-richtext' />
                Create from document
              </Button>
            </div>
          </ScopedBlock>
          <div className='d-flex align-items-center gap-2 border rounded zocks-border-color-gray-500 px-2'>
            <Form.Control
              type='text'
              list='rooms'
              value={contactFilter}
              onChange={(e) => handleContactFilterChange(e.currentTarget.value)}
              placeholder='Search in contacts'
              className='border-0 px-0'
            />
            <i className='bi bi-search' />
          </div>
        </Col>
      </Row>
      <Row className='mb-2'>
        <Col className='d-flex flex-column align-items-end'>
          <Form.Check
            checked={showOnlyMyContacts}
            onChange={(e) => {
              setShowOnlyMyContacts(e.currentTarget.checked)
              setCookie('showOnlyMyContacts', e.currentTarget.checked, { path: '/', maxAge: SHOW_ONLY_MY_CONTACTS_COOKIE_MAX_AGE })
            }}
            type='checkbox'
            id='show-only-my-contacts'
            label={`Show only my contacts`}
          />
        </Col>
      </Row>
      <Row>
        <Col className='p-0'>
          <ZocksDataTable
            columns={columns}
            data={contacts}
            progressPending={isFetching}
            pagination
            paginationServer
            paginationTotalRows={contactsResponse?.page.totalElements}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
          />
        </Col>
      </Row>
    </Container>
  )
}
